import React, { createContext, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Script from 'next/script';
import { useSessionCustomer } from '../customer';
import { renderSnippet } from './segment';

/**
 * @returns {import('./Reporter').Reporter}
 */
export const useReporter = () => useContext(ReporterContext);

/**
 * @param {string} page
 */
export const useTrackPage = page => {
  const reporter = useReporter();

  useEffect(() => {
    reporter.page(page);
  }, [reporter, page]);
};

export const ReporterContext = createContext(null);

export const ReporterProvider = ({ reporter, children }) => {
  const { customer } = useSessionCustomer();

  useEffect(() => {
    if (customer?.id && customer?.email) {
      reporter.user({ id: customer.id, email: customer.email });
    }
  }, [customer?.id, customer?.email, reporter]);

  return (
    <ReporterContext.Provider value={reporter}>
      <Script
        id="segment-script"
        dangerouslySetInnerHTML={{ __html: renderSnippet() }}
      />
      {children}
    </ReporterContext.Provider>
  );
};

ReporterProvider.displayName = 'ReporterProvider';

ReporterProvider.propTypes = {
  reporter: PropTypes.object.isRequired,
  children: PropTypes.node,
};

export const PrefixedReporterProvider = ({ prefix, children }) => {
  const reporter = useReporter();
  const prefixedReporter = useMemo(() => reporter.withPrefix(prefix), [
    reporter,
    prefix,
  ]);

  return (
    <ReporterContext.Provider value={prefixedReporter}>
      {children}
    </ReporterContext.Provider>
  );
};

PrefixedReporterProvider.displayName = 'PrefixedReporterProvider';

PrefixedReporterProvider.propTypes = {
  prefix: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};
